/**
 * Extracts only digit characters from a string
 * @param input - The string to extract digits from
 * @returns A string containing only digit characters (0-9)
 *
 * @example
 * ```typescript
 * extractDigits('01 / 2030'); // Returns "012030"
 * extractDigits('abc123def'); // Returns "123"
 * extractDigits(''); // Returns ""
 * ```
 */
export function extractDigits(input: string): string {
  return input.replace(/\D/g, '');
}

/**
 * Checks if a string contains only digit characters
 * @param input - The string to check
 * @returns True if the string contains only digits, false otherwise
 *
 * @example
 * ```typescript
 * isDigit('123'); // Returns true
 * isDigit('12a'); // Returns false
 * isDigit(''); // Returns false
 * ```
 */
export function isDigit(input: string): boolean {
  return input.length > 0 && /^\d+$/.test(input);
}

/**
 * Parses an amount that may arrive as a string or a number, guarding against non-numeric input.
 * @param value - The amount as a string (e.g. "12") or a number
 * @returns The parsed number, or undefined when it is not a finite number
 */
export function parseAmount(value: string | number): number | undefined {
  const amount = typeof value === 'number' ? value : parseFloat(value);

  return Number.isFinite(amount) ? amount : undefined;
}

/**
 * Restricts a value to a given range
 * @param value - The value to clamp
 * @param min - The lower bound of the range
 * @param max - The upper bound of the range
 * @returns The value restricted to the [min, max] range
 *
 * @example
 * ```typescript
 * clamp(5, 0, 10); // Returns 5
 * clamp(-5, 0, 10); // Returns 0
 * clamp(15, 0, 10); // Returns 10
 * ```
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Linear interpolation function: it maps a value from one numeric range to another
 * @param value - The value to map, expressed in the current scale
 * @param currentScaleMin - The lower bound of the current scale
 * @param currentScaleMax - The upper bound of the current scale
 * @param newScaleMin - The lower bound of the new scale
 * @param newScaleMax - The upper bound of the new scale
 * @returns The value mapped onto the new scale
 *
 * @example
 * ```typescript
 * normalize(50, 0, 100, 0, 1); // Returns 0.5
 * normalize(5, 0, 10, -1, 1); // Returns 0
 * normalize(5, 3, 3, 0, 1); // Returns 0 (degenerate current scale)
 * ```
 */
export function normalize(
  value: number,
  currentScaleMin: number,
  currentScaleMax: number,
  newScaleMin: number,
  newScaleMax: number,
): number {
  if (currentScaleMax === currentScaleMin) return newScaleMin;

  const ratio = (value - currentScaleMin) / (currentScaleMax - currentScaleMin);

  return (newScaleMax - newScaleMin) * ratio + newScaleMin;
}
